import React, { useState, useRef, useEffect } from 'react';
import { HiSun, HiMoon, HiCog6Tooth, HiArrowRightOnRectangle } from 'react-icons/hi2';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../providers/AuthProvider';
import { useSettings } from '../../providers/SettingsProvider';
import { ProfileModal } from '../modals/ProfileModal';
import { ConfirmDialog } from './ConfirmDialog';

interface ProfileDropdownProps {
  className?: string;
  /**
   * Position of the dropdown menu:
   * - 'sidebar': Opens to the right of the avatar (for desktop sidebar)
   * - 'header': Opens below and aligned to the right edge (for mobile header)
   */
  position?: 'sidebar' | 'header';
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  className = '',
  position = 'sidebar'
}) => {
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // V2 auth state
  const { user: authUser, logout } = useAuth();

  // V2 settings state for additional user info
  const { currentUser } = useSettings();

  // Merge user data - prefer V2 auth, fallback to settings currentUser
  const userName =
    authUser?.name || authUser?.email || currentUser?.name || currentUser?.email || 'User';
  const userEmail = authUser?.email || currentUser?.email;
  const userImage = authUser?.image || currentUser?.image;

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Get avatar initials
  const getInitials = () => {
    const name = authUser?.name || currentUser?.name;
    const email = authUser?.email || currentUser?.email;
    if (name) {
      return name.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return '?';
  };

  const handleEditProfile = () => {
    setIsOpen(false);
    setShowProfileModal(true);
  };

  const handleLogout = () => {
    setIsOpen(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  // Get dropdown position classes based on position prop
  const getDropdownPositionClasses = () => {
    if (position === 'sidebar') {
      // Open to the right of the avatar, vertically centered
      return 'left-full ml-2 bottom-0';
    } else {
      // Open below and aligned to the right edge (for mobile header)
      return 'top-full mt-2 right-0';
    }
  };

  return (
    <>
      <div
        className={`relative ${className}`}
        ref={dropdownRef}
        data-testid="profile-dropdown"
        data-position={position}
      >
        {/* Avatar Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Profile menu"
          aria-expanded={isOpen}
          aria-haspopup="true"
          className="w-full flex items-center justify-center py-3 px-2 hover:bg-vb-surface-hover transition-colors dark:hover:bg-vb-surface-hover-dark"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-vb-primary to-purple-600 flex items-center justify-center overflow-hidden">
            {userImage ? (
              <img src={userImage} alt={userName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-sm font-semibold">{getInitials()}</span>
            )}
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className={`absolute ${getDropdownPositionClasses()} w-56 bg-vb-surface dark:bg-vb-surface-dark border border-vb-border dark:border-vb-border-dark rounded-lg shadow-lg py-2 z-[100]`}
            role="menu"
            aria-orientation="vertical"
          >
            {/* User Info */}
            <div className="px-4 py-2 border-b border-vb-border dark:border-vb-border-dark">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-vb-primary to-purple-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {userImage ? (
                    <img src={userImage} alt={userName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-sm font-semibold">{getInitials()}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark truncate">
                    {userName}
                  </div>
                  {userEmail && userName !== userEmail && (
                    <div className="text-xs text-vb-text-secondary dark:text-vb-text-secondary-dark truncate">
                      {userEmail}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={() => {
                toggleTheme();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors"
              role="menuitem"
            >
              {theme === 'dark' ? (
                <HiSun className="w-5 h-5 text-vb-text-secondary dark:text-vb-text-secondary-dark" />
              ) : (
                <HiMoon className="w-5 h-5 text-vb-text-secondary dark:text-vb-text-secondary-dark" />
              )}
              <span className="text-sm text-vb-text-primary dark:text-vb-text-primary-dark">
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </span>
            </button>

            {/* User Settings */}
            <button
              onClick={handleEditProfile}
              className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors"
              role="menuitem"
            >
              <HiCog6Tooth className="w-5 h-5 text-vb-text-secondary dark:text-vb-text-secondary-dark" />
              <span className="text-sm text-vb-text-primary dark:text-vb-text-primary-dark">
                User Settings
              </span>
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-vb-border dark:border-vb-border-dark" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors"
              role="menuitem"
            >
              <HiArrowRightOnRectangle className="w-5 h-5 text-vb-danger" />
              <span className="text-sm text-vb-danger">Log Out</span>
            </button>
          </div>
        )}
      </div>

      {/* Profile Modal */}
      <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />

      {/* Logout Confirmation */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        title="Log Out"
        message="Are you sure you want to log out?"
        confirmText="Log Out"
        variant="danger"
      />
    </>
  );
};
